import { Component, OnInit, ChangeDetectorRef, Output, EventEmitter, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { ProfessionalShift } from '../../../../core/models/professional.model';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AppDropdown } from '../../../../shared/components/dropdown/dropdown';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';

@Component({
    selector: 'app-list-of-shifts',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        AppPrimeTable,
        AppButton,
        InputTextModule,
        DatePickerModule,
        IconFieldModule,
        InputIconModule,
        AppDropdown,
        HasPermissionDirective
    ],
    templateUrl: './list-of-shifts.html',
})
export class ListOfShifts implements OnInit {
    @Output() newShift = new EventEmitter<void>();
    @Output() editShift = new EventEmitter<ProfessionalShift>();
    @Output() deleteShift = new EventEmitter<ProfessionalShift>();

    shifts: ProfessionalShift[] = [];
    loading = true;

    // Pagination
    currentPage = 1;
    pageSize = 10;
    totalRecords = 0;
    searchText = '';

    // Date filters
    startDate: Date | null = null;
    endDate: Date | null = null;

    cols: PrimeTableColumn[] = [
        { field: 'doctor_name', header: 'professionals_doctor_name', type: 'custom' },
        { field: 'shift_title', header: 'professionals_shift_title' },
        { field: 'start_date', header: 'professionals_date_start', type: 'custom' },
        { field: 'end_date', header: 'professionals_date_end', type: 'custom' },
        { field: 'start_time', header: 'professionals_time_start', type: 'custom' },
        { field: 'end_time', header: 'professionals_time_end', type: 'custom' },
        { field: 'duration', header: 'professionals_duration', type: 'custom' },
        { field: 'office_name', header: 'office', type: 'custom' },
        { field: 'shiftActions', header: 'table_column_actions', type: 'action', width: '100px' }
    ];

    @ViewChild('doctor_name') doctorNameTemplate!: TemplateRef<any>;
    @ViewChild('date_cell') dateCellTemplate!: TemplateRef<any>;
    @ViewChild('time_cell') timeCellTemplate!: TemplateRef<any>;
    @ViewChild('duration_cell') durationCellTemplate!: TemplateRef<any>;
    @ViewChild('shiftActions') shiftActionsTemplate!: TemplateRef<any>;

    templates: { [key: string]: TemplateRef<any> } = {};

    constructor(
        private professionalsService: ProfessionalsService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit() {
        this.loadShifts();
    }

    ngAfterViewInit() {
        this.templates = {
            'doctor_name': this.doctorNameTemplate,
            'start_date': this.dateCellTemplate,
            'end_date': this.dateCellTemplate,
            'start_time': this.timeCellTemplate,
            'end_time': this.timeCellTemplate,
            'duration': this.durationCellTemplate,
            'shiftActions': this.shiftActionsTemplate
        };
        this.cdr.detectChanges();
    }

    async loadShifts() {
        this.loading = true;
        this.cdr.detectChanges();

        const offset = (this.currentPage - 1) * this.pageSize;

        try {
            const startStr = this.startDate ? this.startDate.toISOString().split('T')[0] : '';
            const endStr = this.endDate ? this.endDate.toISOString().split('T')[0] : '';

            const { data, error } = await this.professionalsService.getProfessionalShifts(
                this.pageSize,
                offset,
                startStr,
                endStr,
                this.searchText
            );

            if (data) {
                this.shifts = data.schedules || [];
                this.totalRecords = (data.total_pages || 0) * this.pageSize;
            } else {
                console.error('Error fetching shifts', error);
                this.shifts = [];
                this.totalRecords = 0;
            }
        } catch (e) {
            console.error('Exception fetching shifts', e);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    refresh() {
        this.loadShifts();
    }

    onSearch() {
        if (this.searchText.length === 0 || this.searchText.length >= 3) {
            this.currentPage = 1;
            this.loadShifts();
        }
    }

    onDateChange() {
        this.currentPage = 1;
        this.loadShifts();
    }

    onPageChange(event: any) {
        this.currentPage = (event.first / event.rows) + 1;
        this.pageSize = event.rows;
        this.loadShifts();
    }

    onNewShift() {
        this.newShift.emit();
    }

    onEdit(shift: ProfessionalShift) {
        this.editShift.emit(shift);
    }

    onDelete(shift: ProfessionalShift) {
        this.deleteShift.emit(shift);
    }

    formatDate(dateString: string): string {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    }

    formatTime(timeString: string): string {
        if (!timeString) return '-';
        return timeString.substring(0, 5); // HH:MM format
    }

    clearFilters() {
        this.searchText = '';
        this.startDate = null;
        this.endDate = null;
        this.currentPage = 1;
        this.loadShifts();
    }
}
